/*
 * Report-shaping hook (U25). Co-located here so the shared `src/api/queries.ts`
 * stays untouched — this wraps the shared `useRunInconsistencies(runId)` query
 * and derives the grouped-by-verdict view model the report renders.
 *
 * The backend's `inconsistencies` array is the discrepancy rows only
 * (`verified_ok` / `failed_confirmed` are NOT discrepancies and never appear —
 * see `InconsistencyReportResponse` docs). So the grouping here only ever
 * contains discrepancy classes; the `clean` flag drives the all-verified state.
 */

import { useMemo } from "react";
import type { InconsistencyRow } from "../../api/client";
import { useRunInconsistencies } from "../../api/queries";
import { verdictCopy, verdictOrderIndex } from "./verdictCopy";

export interface VerdictGroup {
  /** The raw backend classification string (e.g. `count_mismatch`). */
  classification: string;
  /** How many discrepancy rows carry this classification. */
  count: number;
  /** The rows themselves, in their original order. */
  rows: InconsistencyRow[];
  /** Whether this verdict warrants a recovery action (always true here). */
  actionable: boolean;
}

export interface VerdictReportView {
  /** Discrepancy rows grouped by classification, in display order. */
  groups: VerdictGroup[];
  /** Total discrepancy rows across all groups. */
  totalDiscrepancies: number;
  /** True when the run is clean (zero discrepancies). */
  clean: boolean;
  /** True when at least one ad was still PENDING_REVIEW at audit time. */
  recheckRecommended: boolean;
}

/** Group an inconsistency row array by classification, in display order. */
export function groupByVerdict(rows: InconsistencyRow[]): VerdictGroup[] {
  const byClass = new Map<string, InconsistencyRow[]>();
  for (const row of rows) {
    const bucket = byClass.get(row.classification);
    if (bucket) bucket.push(row);
    else byClass.set(row.classification, [row]);
  }
  return Array.from(byClass.entries())
    .map(([classification, groupRows]) => ({
      classification,
      count: groupRows.length,
      rows: groupRows,
      actionable: verdictCopy(classification).actionable,
    }))
    .sort(
      (a, b) =>
        verdictOrderIndex(a.classification) -
          verdictOrderIndex(b.classification) ||
        a.classification.localeCompare(b.classification),
    );
}

/**
 * The run's inconsistency report shaped for the UI. Returns the underlying
 * query state (loading/error/refetch) alongside the derived `view`, which is
 * `null` until data arrives.
 */
export function useVerdictReport(runId: string) {
  const query = useRunInconsistencies(runId);

  const view = useMemo<VerdictReportView | null>(() => {
    const data = query.data;
    if (!data) return null;
    const groups = groupByVerdict(data.inconsistencies);
    return {
      groups,
      totalDiscrepancies: data.inconsistencies.length,
      clean: data.clean,
      recheckRecommended: data.recheck_recommended,
    };
  }, [query.data]);

  return { ...query, view };
}
